<!-- eslint-disable -->

<template>
  <div class="main">
    <QuoteUserMessages />
    <div class="quote-sizing-list container mt-5">
        <SearchDetailsHeaderForm
          :advancedSearch="advancedSearch"
          :initialRouteQuery="initialRouteQuery"
        />
        <QuotesTable
          :quotes="quotesList"
          :groupedMode="!filtersActive"
          :loading="fetchingRootQuotes || fetchingChildQuotes"
        />
        <!-- (HTML from PrimVue Paginator) -->
        <div
          class="p-paginator p-component"
          data-pc-name="paginator"
          data-pc-section="root"
          pv_id_71=""
          >

          <!-- Prev -->
          <button
            class="p-paginator-prev p-paginator-element p-link"
            type="button"
            aria-label="Previous Page"
            @click="prevPage"
            :disabled="prevPageDisabled"
            :style="{ color: prevPageDisabled ? 'var(--gray-600)' : 'var(--gray-900)' }"
            >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              class="p-icon p-paginator-icon"
              >
              <path
                d="M8.75 11.185C8.65146 11.1854 8.55381 11.1662 8.4628 11.1284C8.37179 11.0906 8.28924 11.0351 8.22 10.965L4.72 7.46496C4.57955 7.32433 4.50066 7.13371 4.50066 6.93496C4.50066 6.73621 4.57955 6.54558 4.72 6.40496L8.22 2.93496C8.36095 2.84357 8.52851 2.80215 8.69582 2.81733C8.86312 2.83252 9.02048 2.90344 9.14268 3.01872C9.26487 3.134 9.34483 3.28696 9.36973 3.4531C9.39463 3.61924 9.36303 3.78892 9.28 3.93496L6.28 6.93496L9.28 9.93496C9.42045 10.0756 9.49934 10.2662 9.49934 10.465C9.49934 10.6637 9.42045 10.8543 9.28 10.995C9.13526 11.1257 8.9448 11.1939 8.75 11.185Z"
                fill="currentColor">
              </path>
            </svg>
          </button>

          <span
            class="p-paginator-current"
            data-pc-section="current"
            aria-live="polite"
            >
            Page {{ page }} of {{ totalPages }}
          </span>

          <!-- Next -->
          <button
            class="p-paginator-next p-paginator-element p-link"
            type="button"
            aria-label="Next Page"
            @click="nextPage"
            :disabled="nextPageDisabled"
            :style="{ color: nextPageDisabled ? 'var(--gray-600)' : 'var(--gray-900)' }"
            >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              class="p-icon p-paginator-icon"
              >
              <path d="M5.25 11.1728C5.14929 11.1694 5.05033 11.1455 4.9592 11.1025C4.86806 11.0595 4.78666 10.9984 4.72 10.9228C4.57955 10.7822 4.50066 10.5916 4.50066 10.3928C4.50066 10.1941 4.57955 10.0035 4.72 9.86283L7.72 6.86283L4.72 3.86283C4.66067 3.71882 4.64765 3.55991 4.68275 3.40816C4.71785 3.25642 4.79932 3.11936 4.91585 3.01602C5.03238 2.91268 5.17819 2.84819 5.33305 2.83149C5.4879 2.81479 5.64411 2.84671 5.78 2.92283L9.28 6.42283C9.42045 6.56346 9.49934 6.75408 9.49934 6.95283C9.49934 7.15158 9.42045 7.34221 9.28 7.48283L5.78 10.9228C5.71333 10.9984 5.63193 11.0595 5.5408 11.1025C5.44966 11.1455 5.35071 11.1694 5.25 11.1728Z" fill="currentColor">
              </path>
            </svg>
          </button>
        </div>
        <!-- (End of HTML from PrimeVue Paginator) -->
    </div>
  </div>
</template>

<script lang="ts" setup>
/* eslint-disable */

import { computed, reactive, nextTick, toRaw, ref, watch } from "vue";
import type { QuotesListAdvancedSearch, StatusItem } from "@/components/quotes/types";
import SearchDetailsHeaderForm from "@/components/quotes/SearchDetailsHeaderForm.vue";
import QuotesTable from "@/components/quotes/QuotesTable.vue";
import QuoteUserMessages from "@/components/quotes/QuoteUserMessages.vue";
import {
  Quote_Status_Dataset_Enum,
  SkCustomer,
  SkNwa,
  SkPriceList,
  SkPlant,
  SkWellAddress,
  SkContract,
  useGetQuotesCountQuery,
  useGetQuotesWithoutLineItemsQuery,
  UsersFieldsFragment,
  type Quotes,
  Order_By,
} from "gql/api";
import { watchDebounced } from "@vueuse/core";
import { sortQuotesWithHierarchy } from "@/utils/quoteHelpers";
import type { QuoteWithChildren } from "@/utils/quoteHelpers";

import useUserInfo from "@/composables/auth/useUserInfo";
import {useRouter} from "vue-router";

type gqlQueryObject = {
  limit?: number | undefined, // TODO: this should be enforced?
  offset: number,
  order_by: {
    created_at: Order_By;
  }[],
  where: {
    deleted_at: { _is_null: boolean },
    parent_id: object, // TOOD: how/why is this an object?
    name: {} | {_ilike: string;},
    sk_proposal_number: {} | {_ilike: string};
    erp_price_list_name: {} | { _ilike: string;},
    erp_nwa_name: {} | {_ilike: string;},
    erp_nwa_id: {} | {_eq: string;},
    prepared_by_id: {} | {_ilike: string;},
    erp_contract_number: {} | {_eq: any;}, // TODO: should not be any?
    erp_plant_name: {} | {_ilike: string;},
    erp_well_name: {} | {_ilike: string;},
    erp_address_name: {} | {_ilike: string;},
    erp_customer_name: {} | {_ilike: string;},
    status: {} | {_eq: Quote_Status_Dataset_Enum;},
    pricing_date: {} | {_eq: string;}
  }
}

type vueRouterQueryObject = {
  page: string;
  search?: string;
  proposal?: string;
  nwa?: string;
  priceList? : string;
  contract?: string;
  district?: string;
  user?: string;
  well?: string;
  address?: string;
  wellAddress?: string;
  customer?: string | number;
  status?: string;
  pricingDate?: string;
}

const router = useRouter();
const user = useUserInfo();
const initialRouteQuery = router.currentRoute.value.query;

if (user.is_customer) {
  nextTick().then(() => router.push({name: "NotFound"}));
}

/**
 * Creates a map of root quotes with their children
 * Used for unfiltered/grouped mode display
 */
const createRootQuoteMap = (rootQuotes: Quotes[], childQuotes: Quotes[]): Record<string, Quotes & QuoteWithChildren> => {
  // Combine all quotes for grouping
  const allQuotes = [...rootQuotes, ...childQuotes];
  // Group by parent chain (parent_id or self if orphaned)
  const parentGroups = new Map<string, Quotes[]>();
  allQuotes.forEach(quote => {
    const parentId = quote.parent?.id;
    const groupKey = parentId || quote.id;
    const group = parentGroups.get(groupKey) || [];
    group.push(quote);
    parentGroups.set(groupKey, group);
  });
  const rootQuotesMap: Record<string, Quotes & QuoteWithChildren> = {};
  parentGroups.forEach((group) => {
    if (group.length === 1) {
      const quote = group[0];
      // If orphaned (has parent but parent not in results), make it a root
      if (quote.parent && quote.parent.id && !allQuotes.some(q => q.id === quote.parent?.id)) {
        rootQuotesMap[quote.id] = { ...quote, parent: null, children: [] };
      } else if (!quote.parent) {
        rootQuotesMap[quote.id] = { ...quote, children: [] };
      }
    } else {
      // Multiple siblings: highest version_number is root, others are children
      const sortedGroup = [...group].sort((a, b) => (b.version_number || 0) - (a.version_number || 0));
      const root = { ...sortedGroup[0], parent: null, children: sortedGroup.slice(1).map(child => ({ ...child, parent: sortedGroup[0] })) };
      rootQuotesMap[root.id] = root;
    }
  });
  return rootQuotesMap;
};

/**
 * Groups quotes by their parent_id and creates dirty roots when needed
 * Used for filtered mode display
 */
const groupFilteredQuotes = (quotes: Quotes[]): (Quotes & QuoteWithChildren)[] => {
  // Group quotes by parent_id (or self if orphaned)
  const parentGroups = new Map<string, Quotes[]>();

  quotes.forEach(quote => {
    const parentId = quote.parent?.id;
    // If quote has no parent, or its parent is not in the result set, group by its own id (orphan/true root)
    const groupKey = parentId || quote.id;
    const group = parentGroups.get(groupKey) || [];
    group.push(quote);
    parentGroups.set(groupKey, group);
  });

  const result: (Quotes & QuoteWithChildren)[] = [];

  parentGroups.forEach((group) => {
    if (group.length === 1) {
      // Single quote in group - add as is
      const quote = group[0];
      // If orphaned (has parent but parent not in results), make it a dirty root
      if (quote.parent && quote.parent.id && !quotes.some(q => q.id === quote.parent?.id)) {
        result.push({
          ...quote,
          parent: null,
          children: []
        });
      } else {
        result.push(quote);
      }
    } else {
      // Multiple siblings: highest version_number is root, others are children
      const sortedGroup = [...group].sort((a, b) => (b.version_number || 0) - (a.version_number || 0));
      const dirtyRoot = {
        ...sortedGroup[0],
        parent: null,
        children: sortedGroup.slice(1).map(child => ({
          ...child,
          parent: sortedGroup[0]
        }))
      };
      result.push(dirtyRoot);
    }
  });

  return result;
};

const STATUS_OPTIONS = Object.values(Quote_Status_Dataset_Enum)
  .map((value) => ({
    value,
    label: value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
  }))

const getInitialQueryValue = (key: string) => {
  const value = router.currentRoute.value.query?.[key];
  return Array.isArray(value) ? value[0] : value;
}

const getInitialPageValue = () => {
  const val = getInitialQueryValue('page');
  return val && !isNaN(parseFloat(val)) && isFinite(parseFloat(val)) ? Number(val) : 1;
};

const getInitialCustomer = () => {
  const customer = getInitialQueryValue('customer');
  if (customer) {
    return {
      id: isNaN(Number(customer)) ? "" : Number(customer),
      name: customer // We'll update this with the real customer name when we fetch it in SearchDetailsHeaderForm
    } as SkCustomer;
  }
  return "";
}

const getInitalStatus = () => {
  const val = router.currentRoute.value.query?.status;
  return val ? STATUS_OPTIONS.find(item => item.value === val) || "" : "";
};

const getInitialPricingDate = () => {
    const pricingDate = getInitialQueryValue('pricingDate');
    if (pricingDate) {
      const date = new Date(pricingDate);
      date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
      return date;
    }
    return null;
}

const getGqlPricingDate = (pricingDate: Date) => {
  return pricingDate.getFullYear()
    + "-"
    + String(pricingDate.getMonth() + 1).padStart(2, '0')
    + "-"
    + String(pricingDate.getDate()).padStart(2, '0');
}

const page = ref<number>(getInitialPageValue());
const advancedSearch = reactive<QuotesListAdvancedSearch>({
  // Dev note: variable behavior with advancedSearch and router query not 1:1
  quoteSearch: getInitialQueryValue('search') || "",
  proposalSearch: getInitialQueryValue('proposal') || "",
  nwaSearch: getInitialQueryValue('nwa') || "",
  priceListSearch: getInitialQueryValue('priceList') || "",
  contractSearch: getInitialQueryValue("contract") || "",
  salesDistrictSearch: getInitialQueryValue("district") || "",
  userSearch: "",
  wellSearch: "", // TODO: revisting later
  addressSearch: "", // TODO: revisiting later
  wellAddressSearch: getInitialQueryValue("wellAddress") || "",
  customerSearch: getInitialCustomer() || "",
  statusSearch: getInitalStatus() || "",
  pricingDate: getInitialPricingDate() || null,
});


const getQuoteGqlQueryObject = (
  _parent_id: object = {},
  limit: number = 10,
  order_by_created_at: Order_By = Order_By.Desc
): gqlQueryObject => {
  // These type guards will help narrow down our intentions. However, it doesn't change
  // the underlying mechanism that's morphing these properties from objects to strings.
  // Depending on who you ask this could be problematic. Just making a note for later.
  const quoteName = advancedSearch.quoteSearch?.trim();
  const proposalNumber = advancedSearch.proposalSearch?.trim();
  const priceList = (advancedSearch.priceListSearch as SkPriceList)?.name || advancedSearch.priceListSearch;
  // Filter by NWA id when an NWA is selected: names on older quotes are often
  // retired ECC names, while the id is shared by ECC and S4.
  const nwaId = (advancedSearch.nwaSearch as SkNwa)?.id
    || (/^\d+$/.test(String(advancedSearch.nwaSearch || "").trim())
      ? String(advancedSearch.nwaSearch).trim()
      : "");
  const nwa = nwaId ? "" : (advancedSearch.nwaSearch as SkNwa)?.name || advancedSearch.nwaSearch;
  const preparedBy = (advancedSearch.userSearch as UsersFieldsFragment)?.id;
  const plant = (advancedSearch.salesDistrictSearch as SkPlant)?.name || advancedSearch.salesDistrictSearch;
  const wellAddress = (advancedSearch.wellAddressSearch as SkWellAddress)?.well?.name?.trim() || advancedSearch.wellAddressSearch;
  const well = (advancedSearch.wellSearch as SkWellAddress)?.well?.name?.trim() || advancedSearch.wellSearch || wellAddress; // TODO: revisting
  const address = (advancedSearch.addressSearch as SkWellAddress)?.address?.name?.trim() || advancedSearch.addressSearch; // TODO: revisting
  const customer = (advancedSearch.customerSearch as SkCustomer)?.name?.trim() || advancedSearch.customerSearch;
  const status = (advancedSearch.statusSearch as StatusItem)?.value;
  const contractNumber = (advancedSearch.contractSearch as SkContract)?.number || advancedSearch.contractSearch;
  const pricingDate = advancedSearch.pricingDate
    ? getGqlPricingDate(advancedSearch.pricingDate)
    : null;

  // Dev note: if the filter property does not exist, fallback to an empty object.
  // Dev note 2: using _iregex for well and address to allow for flexible whitespace matching.
  // ^ This is important because quote data contains SK data with inconsistent whitespace.
  return {
    ...(limit !== -1 && {limit}),
    offset: page.value >= 1 ? (page.value - 1) * 10 : 0,
    order_by: [{ created_at: order_by_created_at }],
    where: {
      deleted_at: { _is_null: true },
      parent_id: _parent_id,
      name: quoteName ? { _ilike: `%${quoteName}%` } : {},
      sk_proposal_number: proposalNumber ? { _ilike: `%${proposalNumber}%` } : {},
      erp_price_list_name: priceList ? { _ilike: `%${priceList}%` } : {},
      erp_nwa_name: nwa ? { _ilike: `%${nwa}%` } : {},
      erp_nwa_id: nwaId ? { _eq: String(nwaId) } : {},
      prepared_by_id: preparedBy ? { _ilike: `%${preparedBy}%` } : {},
      erp_contract_number: contractNumber ? { _ilike: `%${contractNumber}%` } : {},
      erp_plant_name: plant ? { _ilike: `%${plant}%`} : {},
      erp_well_name: well ? { _iregex: `${well.toString().replace(/ /g, '\\s+')}` } : {},
      erp_address_name: address ? { _iregex: `${address.toString().replace(/ /g, '\\s+')}` } : {},
      erp_customer_name: customer ? { _ilike: `%${customer}%` } : {},
      status: status ? { _eq: status } : {},
      pricing_date: pricingDate ? { _eq: pricingDate } : {}
    }
  };
};

const quoteGqlQueryObject = computed(() => {
  return getQuoteGqlQueryObject();
})

const { data: getQuotesCount, executeQuery: refetchQuoteCount } = useGetQuotesCountQuery({
  variables: computed(() => {
    const where = structuredClone(toRaw(quoteGqlQueryObject.value.where))
    // Always override parent_id filter for root count
    where.parent_id = { _is_null: true }; // TODO: confirm if needed
    return { where };
  })
});

// 1. Detect if any filters are active (except page)
const filtersActive = computed(() => {
  return !!(
    advancedSearch.quoteSearch?.trim() ||
    advancedSearch.proposalSearch?.trim() ||
    advancedSearch.nwaSearch ||
    advancedSearch.priceListSearch ||
    advancedSearch.contractSearch ||
    advancedSearch.salesDistrictSearch ||
    (advancedSearch.userSearch as UsersFieldsFragment)?.id ||
    advancedSearch.wellSearch ||
    advancedSearch.addressSearch ||
    advancedSearch.wellAddressSearch ||
    advancedSearch.customerSearch ||
    (advancedSearch.statusSearch as StatusItem)?.label ||
    advancedSearch.pricingDate
  );
});

// 2. Queries
const { data: rootQuotes, executeQuery: refetchRootQuotes, fetching: fetchingRootQuotes } = useGetQuotesWithoutLineItemsQuery({
  variables: computed(() => {
    const rootQuoteQuery = structuredClone(toRaw(quoteGqlQueryObject.value))
    // Only filter by parent_id when not in filtered mode
    rootQuoteQuery.where.parent_id = filtersActive.value ? {} : { _is_null: true };
    return rootQuoteQuery;
  })
});

const { data: childQuotes, executeQuery: refetchChildQuotes, fetching: fetchingChildQuotes } = useGetQuotesWithoutLineItemsQuery({
  variables: computed(() => {
    const parentIds = rootQuotes.value?.quotes?.map(quote => quote.id) || [];
    return {
      where: {
        parent_id: { _in: parentIds },
        deleted_at: { _is_null: true }
      },
      order_by: [{created_at: Order_By.Asc}]
    };
  })
});

/**
 * Computed property that handles both filtered and unfiltered quote display modes.
 */
const quotesList = computed(() => {
  if (!rootQuotes.value?.quotes) return [];
  
  if (filtersActive.value) {
    // In filtered mode, group quotes and create dirty roots when needed
    const groupedQuotes = groupFilteredQuotes(rootQuotes.value.quotes as any);
    return sortQuotesWithHierarchy(groupedQuotes as any);
  } else {
    // In grouped mode, return root quotes with their children
    const rootQuotesMap = createRootQuoteMap(
      rootQuotes.value.quotes as any,
      (childQuotes.value?.quotes || []) as any
    );
    return Object.values(rootQuotesMap);
  }
});

const totalPages = computed(() => {
  const count = getQuotesCount?.value?.quotes_aggregate?.aggregate?.count || 0;
  return Math.ceil(count / 10) || 1;
});

const nextPageDisabled = computed(() => {
  return page.value >= totalPages.value;
});

const prevPageDisabled = computed(() => {
  return page.value <= 1;
});

const nextPage = () => {
  if (page.value < totalPages.value) {
    const newPage = page.value + 1;
    const currentQuery = { ...router.currentRoute.value.query };
    currentQuery.page = newPage.toString();
    router.replace({ query: currentQuery });

    // Update the page value after router update
    page.value = newPage;
  }
};

const prevPage = () => {
  if (page.value > 1) {
    const newPage = page.value - 1;
    const currentQuery = { ...router.currentRoute.value.query };
    currentQuery.page = newPage.toString();
    router.replace({ query: currentQuery });

    // Update the page value after router update
    page.value = newPage;
  }
};

watch(advancedSearch, () => {
  page.value = 1; // Reset page value to 1 immediately if user search changes (no debounce)
})

watchDebounced(advancedSearch, () => {
  // Create query object with page 1 and all current filters
  const vueRouterQueryObject: vueRouterQueryObject = { page: "1" };
  const quoteSearch = advancedSearch.quoteSearch.trim();
  const proposalSearch = advancedSearch.proposalSearch.trim();
  const nwa = String((advancedSearch.nwaSearch as SkNwa)?.id || advancedSearch.nwaSearch || "").trim();
  const priceList = ((advancedSearch.priceListSearch as SkPriceList).name || advancedSearch.priceListSearch as string)?.trim();
  const contract = ((advancedSearch.contractSearch as SkContract).number || advancedSearch.contractSearch as string)?.trim();
  const salesDistrictSearch = ((advancedSearch.salesDistrictSearch as SkPlant).name || advancedSearch.salesDistrictSearch as string)?.trim();
  const user = ((advancedSearch.userSearch as UsersFieldsFragment).id)?.trim();
  const well = ((advancedSearch.wellSearch as SkWellAddress)?.well?.id || (advancedSearch.wellSearch as string)).trim();
  const address = ((advancedSearch.addressSearch as SkWellAddress)?.address?.id || (advancedSearch.addressSearch as string)).trim();
  const wellAddress = ((advancedSearch.wellAddressSearch as SkWellAddress)?.address?.id || (advancedSearch.wellAddressSearch as string)).trim();
  const customer = ((advancedSearch.customerSearch as SkCustomer).id?.toString() || advancedSearch.customerSearch as string)?.trim();
  const status = (advancedSearch.statusSearch as StatusItem).value;
  const pricingDate = advancedSearch.pricingDate ? getGqlPricingDate(advancedSearch.pricingDate) : undefined;
  // Add all non-empty filter values to the query
  if (quoteSearch) {
    vueRouterQueryObject.search = quoteSearch;
  }
  if (proposalSearch) {
    vueRouterQueryObject.proposal = proposalSearch;
  }
  if (nwa) {
    vueRouterQueryObject.nwa = nwa;
  }
  if (priceList) {
    vueRouterQueryObject.priceList = priceList;
  }
  if (contract) {
    vueRouterQueryObject.contract = contract;
  }
  if (salesDistrictSearch) {
    vueRouterQueryObject.district = salesDistrictSearch;
  }
  if (user) {
    vueRouterQueryObject.user = user;
  }
  if (well) {
    vueRouterQueryObject.well = well;
  }
  if (address) {
    vueRouterQueryObject.address = address;
  }
  if (wellAddress) {
    vueRouterQueryObject.wellAddress = wellAddress;
  }
  if (customer) {
    vueRouterQueryObject.customer = customer;
  }
  if (status) {
    vueRouterQueryObject.status = status;
  }
  if (pricingDate) {
    vueRouterQueryObject.pricingDate = pricingDate;
  }

  // Update URL with new filters
  router.replace({ query: vueRouterQueryObject });
}, { deep: true, debounce: 400 });

</script>

<style lang="scss" scoped>
.quote-sizing-list {
  --margin-right: 500px;
}

body main.flex-grow-1 {
  background-image: var(--gradient-vertical);
}

.info-box-custom {
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    padding: 5px;
  }
}

.p-autocomplete-component {
  z-index: 900;
}

.p-autocomplete-empty-message {
  margin-left: 10px;
  padding: 8px;
  font-size: 18px;
  line-height: 18px;
}

/* Quote Name Search - Fix width */
.quote-name-autocomplete > input {
  width: 100%;
}

/* Paginator Styles */
.p-paginator {
  display: flex;
  align-items: center;
  justify-content: center;

  .p-paginator-prev,
  .p-paginator-next {
    padding: 11px;
  }

  .p-paginator-prev[disabled],
  .p-paginator-next[disabled] {
    pointer-events: none;
  }

  .p-paginator-current {
    padding-top: 9px;
    color: var(--gray-900);
  }
}
/* End Paginator Styles*/
</style>
